<div id="preloader">
    <div class="jumper">
        <div></div>
        <div></div>
        <div></div>
    </div>
</div>

<div class="page-wrapper bg-red p-t-180 p-b-100 font-robo">
    <div class="wrapper wrapper--w960">
        <section class="inventory-manager-hero" aria-labelledby="inventory-manager-title">
          <div>
            <p class="eyebrow eyebrow-light">Inventory Manager</p>
            <h1 id="inventory-manager-title">Create showroom-ready vehicle listings faster.</h1>
            <p>Use guided fields, media quality prompts, and a live listing preview to publish cleaner car inventory with less rework.</p>
          </div>
          <div class="manager-scorecard" aria-label="Inventory manager highlights">
            <article><strong>3-step</strong><span>listing flow</span></article>
            <article><strong>Live</strong><span>preview mode</span></article>
            <article><strong>Smart</strong><span>field checks</span></article>
          </div>
        </section>

        <div class="inventory-kicker" aria-label="Inventory workflow highlights">
          <span>Inventory cockpit</span>
          <span>Image-ready listings</span>
          <span>Validation built in</span>
        </div>

        <div class="card card-2 inventory-card">
            <div class="card-heading" aria-hidden="true">
              <div class="heading-overlay">
                <strong>2026</strong>
                <span>Showroom-ready vehicle capture</span>
              </div>
            </div>

            <div class="card-body">
                <p class="eyebrow">Inventory Manager</p>
                <h2 class="title">Add New Car</h2>
                <p class="subtitle mb-4">Add inventory quickly with image preview, clear field guidance, and instant validation.</p>

                <div class="form-progress" aria-label="Inventory creation steps">
                  <span class="active">Details</span>
                  <span>Media</span>
                  <span>Publish</span>
                </div>

                <div *ngIf="msg" class="alert alert-danger" role="alert">{{ msg }}</div>

                <form [formGroup]="carForm" (ngSubmit)="submitCar()" novalidate>

                  <div class="manager-workspace">
                    <div class="manager-form-panel">
                      <div class="row">
                    <div class="col-md-6 mb-3">
                      <label class="form-label" for="brand">Brand</label>
                      <input id="brand" formControlName="brand" class="form-control" placeholder="e.g. Toyota" [class.is-invalid]="carForm.get('brand')?.invalid && carForm.get('brand')?.touched" />
                      <div *ngIf="carForm.get('brand')?.invalid && carForm.get('brand')?.touched" class="invalid-feedback">Brand is required</div>
                    </div>

                    <div class="col-md-6 mb-3">
                      <label class="form-label" for="model">Model</label>
                      <input id="model" formControlName="model" class="form-control" placeholder="e.g. Camry Hybrid" [class.is-invalid]="carForm.get('model')?.invalid && carForm.get('model')?.touched" />
                      <div *ngIf="carForm.get('model')?.invalid && carForm.get('model')?.touched" class="invalid-feedback">Model is required</div>
                    </div>

                    <div class="col-md-4 mb-3">
                      <label class="form-label" for="year">Year</label>
                      <div class="form-text">Use 4-digit year (e.g., 2024)</div>
                      <input id="year" type="number" formControlName="year" class="form-control" [class.is-invalid]="carForm.get('year')?.invalid && carForm.get('year')?.touched" />
                      <div *ngIf="carForm.get('year')?.invalid && carForm.get('year')?.touched" class="invalid-feedback">Enter a valid year</div>
                    </div>

                    <div class="col-md-4 mb-3">
                      <label class="form-label" for="price">Price</label>
                      <div class="form-text">Enter a positive price amount</div>
                      <input id="price" type="number" formControlName="price" class="form-control" [class.is-invalid]="carForm.get('price')?.invalid && carForm.get('price')?.touched" />
                      <div *ngIf="carForm.get('price')?.invalid && carForm.get('price')?.touched" class="invalid-feedback">Price is required and must be positive</div>
                    </div>

                    <div class="col-md-12 mb-3">
                      <label class="form-label" for="description">Description</label>
                      <textarea id="description" formControlName="description" class="form-control" rows="3" placeholder="Highlight trim, mileage, condition, and buyer-fit notes."></textarea>
                    </div>

                    <div class="col-md-6 mb-3">
                      <label class="form-label" for="carImage">Upload Image</label>
                      <div class="form-text">Accepted formats: JPG, PNG, WEBP</div>
                      <input id="carImage" type="file" accept="image/*" (change)="onFileChanged($event)" class="form-control" />
                      <div *ngIf="uploadedFileName" class="form-text selected-file">Selected file: {{ uploadedFileName }}</div>
                      <div *ngIf="imgURL" class="mt-2"><img [src]="imgURL" class="img-preview" alt="Selected car preview" /></div>
                    </div>

                    <div class="col-md-6 mb-3">
                      <div class="publish-note">
                        <span>UX tip</span>
                        <p>Use a crisp hero image and buyer-focused description to make each inventory card easier to scan.</p>
                      </div>
                    </div>

                    <div class="col-md-12 text-center mt-3">
                      <button class="btn btn-primary btn-submit" type="submit" [disabled]="carForm.invalid || submitting">
                        <span *ngIf="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Add Car
                      </button>
                    </div>
                      </div>
                    </div>

                    <aside class="manager-preview-panel" aria-label="Live inventory listing preview">
                      <div class="preview-topline">
                        <span>Live preview</span>
                        <strong [class.complete]="carForm.valid">{{ carForm.valid ? 'Ready' : 'Draft' }}</strong>
                      </div>
                      <div class="preview-image-frame">
                        <img *ngIf="imgURL" [src]="imgURL" alt="Selected car preview" />
                        <div *ngIf="!imgURL" class="preview-placeholder">
                          <i class="fas fa-car" aria-hidden="true"></i>
                          <span>Add a vehicle image</span>
                        </div>
                      </div>
                      <p class="preview-label">Upcoming listing</p>
                      <h3>{{ carForm.get('brand')?.value || 'Brand' }} {{ carForm.get('model')?.value || 'Model' }}</h3>
                      <dl class="preview-metrics">
                        <div><dt>Year</dt><dd>{{ carForm.get('year')?.value || '—' }}</dd></div>
                        <div><dt>Price</dt><dd>{{ carForm.get('price')?.value || '—' }}</dd></div>
                      </dl>
                      <div class="quality-list" aria-label="Listing quality checklist">
                        <span [class.done]="carForm.get('brand')?.valid && carForm.get('model')?.valid">Model details</span>
                        <span [class.done]="carForm.get('year')?.valid && carForm.get('price')?.valid">Commercial data</span>
                        <span [class.done]="!!imgURL">Hero image</span>
                      </div>
                    </aside>
                  </div>

                </form>

                <div *ngIf="lastAddedCar" class="card mt-4 success-preview">
                  <div class="card-body d-flex align-items-center gap-3">
                    <img *ngIf="lastAddedCar.imageName" [src]="lastAddedCar.imageName" class="img-preview-sm" alt="Recently added car" />
                    <div>
                      <p class="eyebrow mb-1">Recently added</p>
                      <h5 class="mb-1">{{ lastAddedCar.brand }} {{ lastAddedCar.model }}</h5>
                      <div class="text-muted">Year: {{ lastAddedCar.year }} • Price: {{ lastAddedCar.price }}</div>
                    </div>
                  </div>
                </div>

            </div>
        </div>
    </div>
</div>
